import type { ChatClient } from '@wtfalch/chat';
import { Button, Composer, Icon } from '@wtfalch/design';
import { useCallback, useEffect, useRef, useState } from 'react';

/** How long this composer keeps re-asserting typing before it gives up and
 *  says nothing further -- inside Matrix's own 20-second server-side expiry
 *  (`sendTyping`'s own doc), so the other side never sees "typing" outlive
 *  an idle composer by more than this. */
const TYPING_IDLE_MS = 4000;

/**
 * The box at the bottom of a room or a thread -- `@wtfalch/design`'s
 * `Composer`, `flush`, added expressly to draw the canvas's own shape
 * (design#141): one card as the border, a borderless field inside it, a
 * ruled foot for the toolbar and Send. This file's own Enter-to-send is
 * wired on a wrapping `onKeyDown` -- `Composer` reaches for no key itself,
 * by design, since five surfaces do not agree on what Enter should do.
 *
 * Attaching a file (`sendFile`) is the one thing in the board's toolbar row
 * that has a call behind it. Emoji, "send as mail," "offer a time" -- every
 * other icon the canvas draws there -- do not, and are left out rather than
 * drawn inert.
 *
 * **Typing (#254).** `sendTyping` is not a heartbeat -- Matrix expires it
 * server-side on its own, so a composer that wants the other side to keep
 * seeing "typing" while a person keeps composing has to call it again on
 * further keystrokes (the SDK's own doc). This does, on a plain idle timer
 * rather than on every keystroke: `typingActive`/`typingTimer` track whether
 * `true` was already sent and reset a `TYPING_IDLE_MS` countdown to `false`
 * on each change, so typing without pause sends one `true` and one `false`
 * once it stops, not one call a character. Cleared early, and always, by
 * sending, blurring to empty, or the composer itself unmounting -- a room
 * switch included, since this component is not remounted for one (`roomId`
 * is a dependency, not a `key`, on `Room.tsx`'s own render).
 */
export function RoomComposer({
  client,
  roomId,
  threadId,
  placeholder,
}: {
  client: ChatClient;
  roomId: string;
  threadId: string | null;
  placeholder: string;
}) {
  const [value, setValue] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const typingActive = useRef(false);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopTyping = useCallback(() => {
    if (typingTimer.current) {
      clearTimeout(typingTimer.current);
      typingTimer.current = null;
    }
    if (typingActive.current) {
      typingActive.current = false;
      client.sendTyping(roomId, false);
    }
  }, [client, roomId]);

  const markTyping = useCallback(() => {
    if (!typingActive.current) {
      typingActive.current = true;
      client.sendTyping(roomId, true);
    }
    if (typingTimer.current) clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(stopTyping, TYPING_IDLE_MS);
  }, [client, roomId, stopTyping]);

  // A room switch, or the composer leaving entirely, stops this client
  // telling that room the person is still typing -- `stopTyping` itself
  // changes (and so re-runs this) exactly when `client`/`roomId` do, so the
  // cleanup always stops the *room it was told for*, not whatever `roomId`
  // a later render moved on to.
  useEffect(() => stopTyping, [stopTyping]);

  function send() {
    const text = value.trim();
    if (!text) return;
    stopTyping();
    client.sendMessage(roomId, text, threadId ? { threadId } : undefined);
    setValue('');
    // Send pressed with the mouse took focus off the field: put it back.
    wrapRef.current?.querySelector('textarea')?.focus();
  }

  function attach(files: File[]) {
    for (const file of files) client.sendFile(roomId, file, threadId ? { threadId } : undefined);
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: a keydown listener catching Enter before it reaches the textarea inside `Composer`, not a control of its own -- see the docblock above.
    <div
      ref={wrapRef}
      className="flex-none pt-3 px-5 pb-4 composer"
      onKeyDown={(e) => {
        // Only the textarea itself sends on Enter -- the toolbar's own
        // attach button lives in this same subtree, and without this check
        // pressing Enter to activate it (React Aria's own Space/Enter
        // press handling) also sent whatever text was typed.
        // Enter that accepts an IME candidate is not a send.
        if (e.nativeEvent.isComposing) return;
        if (e.key === 'Enter' && !e.shiftKey && e.target instanceof HTMLTextAreaElement) {
          e.preventDefault();
          send();
        }
      }}
    >
      <Composer
        flush
        value={value}
        onChange={(next) => {
          setValue(next);
          if (next.trim()) markTyping();
          else stopTyping();
        }}
        onSend={send}
        label={placeholder}
        placeholder={placeholder}
        sendDisabled={!value.trim()}
        toolbar={
          <Button
            kind="ghost"
            size="sm"
            aria-label="Attach a file"
            onPress={() => fileRef.current?.click()}
          >
            <Icon name="paperclip" size={16} />
          </Button>
        }
      />
      <input
        ref={fileRef}
        type="file"
        multiple
        hidden
        aria-label="Attach a file"
        onChange={(e) => {
          attach(Array.from(e.target.files ?? []));
          e.target.value = '';
        }}
      />
    </div>
  );
}
